/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import { createLogic } from 'redux-logic';

import {
  captureContactInfoBusinessEvent,
  captureDivCapBusinessEvent,
  captureEmailAddressBusinessEvent
} from 'services/Rest';
import {
  DISPATCH_BUSINESS_EVENT,
  updateComplianceSubscription
} from 'state/preferences/PreferencesActions';
import {
  getCustomerBusinessPartyId,
  getUberId,
  getPrimaryEmailAddress
} from 'state/customer/CustomerSelectors';
import { businessEventDispatchEnabled } from 'services/FeatureFlags';
import {
  CONTACT_INFO_EVENT_TYPES,
  buildContactInfoBusinessEvent
} from 'services/Preferences';
import { getView } from 'state/view/ViewSelectors';

const generateDivCapEventPayload = (
  email,
  bpId,
  uberId,
  distributionOptions
) => {
  const data = {
    type: 'DividendsCapitalGainsEventData'
  };

  if (distributionOptions) {
    data.distributionOptions = distributionOptions;
  }

  return {
    eventType: 'UPDATE_DIVIDENDS_AND_CAPITAL_GAINS',
    emailAddress: email,
    entityKey: {
      businessPartyId: bpId,
      uberId
    },
    data
  };
};

const generateEmailEventPayload = (email, bpId, uberId, previousEmail) => {
  const data = {
    type: 'EmailEventDescriptor'
  };

  const event = {
    eventType: 'ADD_EMAIL_ADDRESS',
    emailAddress: email,
    entityKey: {
      businessPartyId: bpId,
      uberId
    },
    data: {
      ...data,
      preferenceNewValue: email
    }
  };

  if (previousEmail) {
    event.eventType = 'UPDATE_EMAIL_ADDRESS';
    event.data.preferencePreviousValue = previousEmail;
  }

  return event;
};

const hasDispositionResult = (disposition) => {
  return disposition && disposition.result;
};

const BusinessEventLogic = createLogic({
  type: DISPATCH_BUSINESS_EVENT,

  process({ action, getState }, dispatch, done) {
    if (!businessEventDispatchEnabled()) {
      done();
      return;
    }

    const { payload } = action;

    const state = getState();
    const view = getView(state);
    const emailAddressObj = getPrimaryEmailAddress(state);
    const email = emailAddressObj.address;
    const bpId = getCustomerBusinessPartyId(state);
    const uberId = getUberId(state);
    
    // TODO: We need to check if the current business event makes sense for residential/mailing address.
    if (
      (payload.residentialAddress &&
        hasDispositionResult(payload.residentialAddress.disposition)) ||
      (payload.primaryMailingAddress &&
        hasDispositionResult(payload.primaryMailingAddress.disposition))
    ) {
      const businessEvent = buildContactInfoBusinessEvent(
        view,
        email,
        bpId,
        uberId,
        CONTACT_INFO_EVENT_TYPES.UPDATE_POSTAL_ADDRESS
      );
      captureContactInfoBusinessEvent(businessEvent);
    }

    //TODO: Create new Event for Email.
    const { primaryEmailAddress } = payload;
    if (primaryEmailAddress) {
      if (hasDispositionResult(primaryEmailAddress.disposition)) {
        const { previousEmailAddress } = payload;
        const { address: previousEmail } = previousEmailAddress;
        const { address } = primaryEmailAddress;
        const eventPayload = generateEmailEventPayload(
          address,
          bpId,
          uberId,
          previousEmail
        );
        captureEmailAddressBusinessEvent(eventPayload);

        dispatch(updateComplianceSubscription(primaryEmailAddress));
      }
    }

    const updatedPhoneNumbers = payload.phoneNumbers;
    if (updatedPhoneNumbers) {
      const phoneNumbers = updatedPhoneNumbers.filter((phoneNumber) => {
        return hasDispositionResult(phoneNumber.disposition);
      });
      if (phoneNumbers.length > 0) {
        const businessEvent = buildContactInfoBusinessEvent(
          view,
          email,
          bpId,
          uberId,
          CONTACT_INFO_EVENT_TYPES.UPDATE_PHONE
        );
        captureContactInfoBusinessEvent(businessEvent);
      }
    }

    // successful save of divcapgains returns updated arrays without disposition
    const { dividendsCapitalGains } = payload;

    if (dividendsCapitalGains) {
      const { distributionOptions } = dividendsCapitalGains;

      const eventPayload = generateDivCapEventPayload(
        email,
        bpId,
        uberId,
        distributionOptions
      );
      captureDivCapBusinessEvent(eventPayload);
    }

    done();
  }
});

export default BusinessEventLogic;
